import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    AppBar,
    Box,
    Container,
    createTheme,
    ThemeProvider,
    Toolbar,
    Typography,
    Grid,
    Fade,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import Footer from "./Footer";
import Header from "./Header";
import Filters from "./Filters";
import PackageCard from "./components/PackageCard";

const ROOM_TYPES = ["single", "double", "triple", "quad"];

const normalizeValue = (value) => (value ?? "").toString().trim();
const normalizeKey = (value) => normalizeValue(value).toLowerCase();
const toTitleCase = (value) => normalizeValue(value).toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

const computeDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }
    const diff = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
};

const parseCampsSummary = (summary) => {
    if (!summary) return [];
    return summary
        .split("|")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
            const match = part.match(/^(.*)\((.*)\)$/);
            if (!match) {
                return { name: part, available: true };
            }
            const name = match[1].trim();
            const value = match[2].trim();
            const available = value.toLowerCase() !== "n/a" && value !== "";
            return { name, available };
        });
};

const extractCampMeta = (pkg) => {
    const options = new Set();
    const availability = {};

    if (pkg?.campSelection?.availability) {
        Object.entries(pkg.campSelection.availability).forEach(([name, isAvailable]) => {
            const normalized = normalizeKey(name);
            if (!normalized) return;
            options.add(name);
            availability[normalized] = Boolean(isAvailable);
        });
        return { options: [...options], availability };
    }

    if (pkg?.campSelection?.camps?.length) {
        pkg.campSelection.camps.forEach((camp) => {
            const name = normalizeValue(camp?.name);
            const normalized = normalizeKey(name);
            if (!normalized) return;
            options.add(name);
            availability[normalized] = true;
        });
    }

    if (pkg?.campSelection?.missingCamps?.length) {
        pkg.campSelection.missingCamps.forEach((name) => {
            const normalized = normalizeKey(name);
            if (!normalized) return;
            options.add(name);
            if (availability[normalized] === undefined) {
                availability[normalized] = false;
            }
        });
    }

    if (pkg?.campsSummary) {
        parseCampsSummary(pkg.campsSummary).forEach(({ name, available }) => {
            const normalized = normalizeKey(name);
            if (!normalized) return;
            options.add(name);
            if (availability[normalized] === undefined) {
                availability[normalized] = Boolean(available);
            } else {
                availability[normalized] = availability[normalized] || Boolean(available);
            }
        });
    }

    if (pkg?.Camp) {
        const normalized = normalizeKey(pkg.Camp);
        if (normalized) {
            options.add(pkg.Camp);
            availability[normalized] = true;
        }
    }

    return { options: [...options], availability };
};

const isCampAvailable = (pkg, campName) => {
    const normalized = normalizeKey(campName);
    if (!normalized) return false;

    if (pkg?.__campAvailability) {
        const matchKey = Object.keys(pkg.__campAvailability).find(
            (key) => normalizeKey(key) === normalized
        );
        return matchKey ? Boolean(pkg.__campAvailability[matchKey]) : false;
    }

    if (pkg?.campSelection?.availability) {
        const matchKey = Object.keys(pkg.campSelection.availability).find(
            (key) => normalizeKey(key) === normalized
        );
        return matchKey ? Boolean(pkg.campSelection.availability[matchKey]) : false;
    }

    if (pkg?.campSelection?.camps?.length) {
        return pkg.campSelection.camps.some(
            (camp) => normalizeKey(camp?.name) === normalized
        );
    }

    if (pkg?.campsSummary) {
        return parseCampsSummary(pkg.campsSummary).some(
            (camp) => normalizeKey(camp.name) === normalized && camp.available
        );
    }

    if (pkg?.Camp) {
        return normalizeKey(pkg.Camp) === normalized;
    }

    return false;
};

const extractOutboundLocations = (pkg) => {
    const departures = new Set();
    const arrivals = new Set();

    if (pkg?.flights?.flights?.length) {
        pkg.flights.flights.forEach((flight) => {
            const outbound = flight?.outbound || {};
            const departure = normalizeValue(outbound.departureCity);
            const arrival = normalizeValue(outbound.arrivalCity);
            if (departure) departures.add(departure);
            if (arrival) arrivals.add(arrival);
        });
        return { departures: [...departures], arrivals: [...arrivals] };
    }

    Object.keys(pkg || {}).forEach((key) => {
        if (/^#\d+ Outbound Departure$/.test(key)) {
            const value = normalizeValue(pkg[key]);
            if (value) departures.add(value);
        }
        if (/^#\d+ Outbound Arrival$/.test(key)) {
            const value = normalizeValue(pkg[key]);
            if (value) arrivals.add(value);
        }
    });

    return { departures: [...departures], arrivals: [...arrivals] };
};

const normalizeRoomType = (value) => {
    const lower = normalizeKey(value);
    if (lower.includes("single")) return "single";
    if (lower.includes("double")) return "double";
    if (lower.includes("triple")) return "triple";
    if (lower.includes("quad")) return "quad";
    if (lower.includes("quadruple")) return "quad";
    return "";
};

const getAccommodationRoomAvailability = (accommodation) => {
    if (accommodation?.roomTypeAvailability) {
        return accommodation.roomTypeAvailability;
    }

    const availability = {
        single: false,
        double: false,
        triple: false,
        quad: false,
    };

    (accommodation?.rooms || []).forEach((room) => {
        const type = room?.roomTypeCategory || normalizeRoomType(room?.title);
        if (!type) return;
        availability[type] = availability[type] || Boolean(room?.available);
    });

    return availability;
};

const extractAvailableRoomTypes = (pkg) => {
    if (pkg?.roomSelection?.accommodations?.length) {
        const availabilityByAccommodation = pkg.roomSelection.accommodations.map(
            (accommodation) => getAccommodationRoomAvailability(accommodation)
        );

        return ROOM_TYPES.filter((type) =>
            availabilityByAccommodation.every((availability) => availability?.[type])
        );
    }

    const legacyMap = {
        single: "Price for Single",
        double: "Price for Double",
        triple: "Price for Triple",
        quad: "Price for Quad",
    };

    return ROOM_TYPES.filter((type) => {
        const field = legacyMap[type];
        if (!field || pkg?.[field] === undefined) return false;
        const value = parseFloat(pkg[field]);
        return Number.isFinite(value) && value > 0;
    });
};

// Premium Theme Setup
const getTheme = (mode) =>
    createTheme({
        palette: {
            mode,
            primary: { main: "#002366" }, // Teal
            secondary: { main: "#ff9800" },
            background: { default: "#f5f7fa", paper: "#ffffff" },
            text: { primary: "#1e293b", secondary: "#64748b" }, // Modern slate colors
            divider: "rgba(0,0,0,0.06)",
        },
        typography: {
            fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            h1: { fontWeight: 800, letterSpacing: "-0.03em" },
            h2: { fontWeight: 800, letterSpacing: "-0.025em" },
            h3: { fontWeight: 800, letterSpacing: "-0.02em" },
            h4: { fontWeight: 800, letterSpacing: "-0.02em" },
            h5: { fontWeight: 700, letterSpacing: "-0.015em" },
            h6: { fontWeight: 700, letterSpacing: "-0.01em" },
            subtitle1: { fontWeight: 600 },
            subtitle2: { fontWeight: 600 },
            body1: { fontWeight: 400 },
            body2: { fontWeight: 400 },
            button: { fontWeight: 700, textTransform: "none" },
            caption: { fontWeight: 500 },
        },
        shape: { borderRadius: 16 }, // Modern rounded corners
        shadows: [
            "none",
            "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
            "0 3px 6px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)",
            "0 10px 20px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)",
            "0 15px 25px rgba(0,0,0,0.15), 0 5px 10px rgba(0,0,0,0.05)",
            "0 20px 40px rgba(0,0,0,0.2)",
        ],
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        textTransform: 'none',
                        fontWeight: 700,
                        letterSpacing: "0.3px",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    contained: {
                        boxShadow: "0 4px 12px rgba(0,35,102,0.3)",
                        "&:hover": {
                            boxShadow: "0 6px 16px rgba(0,35,102,0.4)",
                            transform: "translateY(-1px)",
                        },
                    },
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 600,
                        borderRadius: 10,
                        letterSpacing: "0.2px",
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: "none",
                    }
                }
            },
        }
    });

// Currency conversion rates (SAR to other currencies - hardcoded as of Jan 10, 2026)
const CURRENCY_RATES = {
    USD: 0.2667,
    GBP: 0.1989,
    EUR: 0.2292,
    CAD: 0.3686,
    AUD: 0.3975,
    CHF: 0.2122,
    JPY: 42.10
};

const CURRENCY_SYMBOLS = {
    SAR: "⃁",
    USD: '$',
    GBP: '£',
    EUR: '€',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'CHF',
    JPY: '¥'
};

export default function PackageExplorer() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Currency state
    const [selectedCurrency, setSelectedCurrency] = useState("GBP");

    // Filter States (Copied from PackagesTable.js)
    const [priceRangeSAR, setPriceRangeSAR] = useState([10000, 50000]);
    const [priceRangeGBP, setPriceRangeGBP] = useState([4000, 10000]);
    const [duration, setDuration] = useState([10, 35]); // Increased max range slightly
    const [categories, setCategories] = useState([]);
    const [categoryTypes, setCategoryTypes] = useState([]);
    const [outboundDepartures, setOutboundDepartures] = useState([]);
    const [outboundArrivals, setOutboundArrivals] = useState([]);
    const [inboundDepartures, setInboundDepartures] = useState([]);
    const [inboundArrivals, setInboundArrivals] = useState([]);
    const [camps, setCamps] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [providers, setProviders] = useState([]);
    const [madinahHotelZone, setMadinahHotelZone] = useState([]);
    const [makkahHotelZone, setMakkahHotelZone] = useState([]);
    const [makkah2HotelZone, setMakkah2HotelZone] = useState([]);
    // Removed unused startDate and endDate state
    // const [startDate, setStartDate] = useState([]);
    // const [endDate, setEndDate] = useState([]);

    // Selected Filter States
    const [selectedProvider, setSelectedProvider] = useState([]);
    const [selectedCamp, setSelectedCamp] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState([]);
    const [selectedCategoryType, setSelectedCategoryType] = useState([]);
    const [selectedOutboundDeparture, setSelectedOutboundDeparture] = useState("");
    const [selectedOutboundArrival, setSelectedOutboundArrival] = useState("");
    const [selectedInboundDeparture, setSelectedInboundDeparture] = useState("");
    const [selectedInboundArrival, setSelectedInboundArrival] = useState("");
    const [selectedMadinahHotelZone, setSelectedMadinahHotelZone] = useState([]);
    const [selectedMakkahHotelZone, setSelectedMakkahHotelZone] = useState([]);
    const [selectedMakkah2HotelZone, setSelectedMakkah2HotelZone] = useState([]);
    const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
    const [selectedStartDate, setSelectedStartDate] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [includeSoldOut, setIncludeSoldOut] = useState(false);

    // Infinite scroll state
    const [displayCount, setDisplayCount] = useState(12);
    const itemsPerLoad = 12;

    // Filter Ranges (Dynamic)
    const [filterBounds, setFilterBounds] = useState({
        minSAR: 0, maxSAR: 100000,
        minGBP: 0, maxGBP: 20000,
        minDur: 5, maxDur: 45
    });

    // Calculate currency bounds dynamically based on selected currency
    const currencyBounds = useMemo(() => {
        if (packages.length === 0) return { min: 0, max: 20000 };

        const rate = CURRENCY_RATES[selectedCurrency];
        if (!rate) return { min: 0, max: 20000 };

        const convertedPrices = packages
            .map(p => p["Starting Price"] * rate)
            .filter(v => v > 0);

        if (convertedPrices.length === 0) return { min: 0, max: 20000 };

        const min = Math.floor(Math.min(...convertedPrices) / 100) * 100;
        const max = Math.ceil(Math.max(...convertedPrices) / 100) * 100;

        return { min, max };
    }, [packages, selectedCurrency]);

    useEffect(() => {
        const fetchPackages = async () => {
            setLoading(true);
            try {
                let data = [];
                const fetchJson = async (url) => {
                    const res = await fetch(url);
                    if (!res.ok) throw new Error(`Failed to load ${url}`);
                    return res.json();
                };

                try {
                    data = await fetchJson("hajj_packages_2026_phase2_status.json");
                } catch (err) {
                    data = await fetchJson("hajj_packages_2026.json");
                }

                const normalizedData = data.map((pkg) => {
                    const normalized = { ...pkg };

                    if (!normalized["Package Name"] && pkg.packageName) {
                        normalized["Package Name"] = pkg.packageName;
                    }
                    if (!normalized.Provider && pkg.serviceProvider) {
                        normalized.Provider = pkg.serviceProvider;
                    }
                    if (!normalized.Category && pkg.category) {
                        normalized.Category = pkg.category;
                    }
                    if (!normalized["Category Type"] && pkg.packageCategoryType) {
                        normalized["Category Type"] = pkg.packageCategoryType;
                    }
                    if (!normalized["Starting Price"] && pkg.startingPriceSar) {
                        const parsedPrice = parseFloat(pkg.startingPriceSar);
                        if (Number.isFinite(parsedPrice)) {
                            normalized["Starting Price"] = parsedPrice;
                        }
                    }
                    if (!normalized["Start Date"] && pkg.startDate) {
                        normalized["Start Date"] = pkg.startDate;
                    }
                    if (!normalized["End Date"] && pkg.endDate) {
                        normalized["End Date"] = pkg.endDate;
                    }
                    if (!normalized["Duration_"]) {
                        const durationDays = computeDurationDays(
                            normalized["Start Date"],
                            normalized["End Date"]
                        );
                        if (durationDays !== null) {
                            normalized["Duration_"] = durationDays;
                        }
                    }
                    if (!normalized["Duration"] && normalized["Duration_"] !== undefined) {
                        normalized["Duration"] = normalized["Duration_"];
                    }
                    if (!normalized.PackageLink_ && pkg.packageSummaryUrl) {
                        normalized.PackageLink_ = pkg.packageSummaryUrl;
                    }
                    if (!normalized["Phase 2 Status"] && pkg.phase2Status) {
                        normalized["Phase 2 Status"] = pkg.phase2Status;
                    }
                    if (!normalized.phase2Status && normalized["Phase 2 Status"]) {
                        normalized.phase2Status = normalized["Phase 2 Status"];
                    }

                    const campMeta = extractCampMeta(normalized);
                    const outboundMeta = extractOutboundLocations(normalized);
                    const availableRoomTypes = extractAvailableRoomTypes(normalized);

                    return {
                        ...normalized,
                        __campOptions: campMeta.options,
                        __campAvailability: campMeta.availability,
                        __outboundDepartures: outboundMeta.departures,
                        __outboundArrivals: outboundMeta.arrivals,
                        __roomTypesAvailable: availableRoomTypes,
                    };
                });

                setPackages(normalizedData);

                // Calculate SAR and duration bounds (currency-independent)
                const sars = normalizedData.map(p => p["Starting Price"]).filter(v => v > 0);
                const durs = normalizedData.map(p => p["Duration_"] || p["Duration"]).filter(v => v > 0);

                if (sars.length && durs.length) {
                    const minSAR = Math.floor(Math.min(...sars) / 1000) * 1000;
                    const maxSAR = Math.ceil(Math.max(...sars) / 1000) * 1000;
                    const minDur = Math.min(...durs);
                    const maxDur = Math.max(...durs);

                    setFilterBounds(prev => ({ ...prev, minSAR, maxSAR, minDur, maxDur }));
                    setPriceRangeSAR([minSAR, maxSAR]);
                    setDuration([minDur, maxDur]);
                }

                setCategories([...new Set(normalizedData.map((pkg) => pkg.Category))].filter(Boolean).sort());
                setCategoryTypes([...new Set(normalizedData.map((pkg) => pkg["Category Type"]))].filter(Boolean).sort());

                // Populate standard lists (simplified for brevity, matching existing logic)
                // Note: For production, these lists should probably be dynamic or constants file
                const campSet = new Set();
                const outboundDepartureSet = new Set();
                const outboundArrivalSet = new Set();
                const roomTypeSet = new Set();

                normalizedData.forEach((pkg) => {
                    (pkg.__campOptions || []).forEach((camp) => {
                        if (camp) campSet.add(camp);
                    });
                    (pkg.__outboundDepartures || []).forEach((departure) => {
                        if (departure) outboundDepartureSet.add(toTitleCase(departure));
                    });
                    (pkg.__outboundArrivals || []).forEach((arrival) => {
                        if (arrival) outboundArrivalSet.add(toTitleCase(arrival));
                    });
                    (pkg.__roomTypesAvailable || []).forEach((type) => {
                        if (type) roomTypeSet.add(type);
                    });
                });

                setOutboundDepartures([...outboundDepartureSet].sort());
                setOutboundArrivals([...outboundArrivalSet].sort());
                setInboundDepartures([]);
                setInboundArrivals([]);

                const zoneSort = (a, b) => {
                    const isOtherA = a.includes('Other');
                    const isOtherB = b.includes('Other');
                    if (isOtherA && !isOtherB) return 1;
                    if (!isOtherA && isOtherB) return -1;
                    return a.localeCompare(b);
                };

                setCamps([...campSet].filter(Boolean).sort());
                setProviders([...new Set(normalizedData.map((pkg) => pkg.Provider))].filter(Boolean).sort());
                setMadinahHotelZone([...new Set(normalizedData.map((pkg) => pkg["Madinah Hotel Zone"]))].filter(Boolean).sort(zoneSort));
                setMakkahHotelZone([...new Set(normalizedData.map((pkg) => pkg["Makkah Hotel Zone"]))].filter(Boolean).sort(zoneSort));
                setMakkah2HotelZone([...new Set(normalizedData.map((pkg) => pkg["Makkah 2 Hotel Zone"]))].filter(Boolean).sort(zoneSort));

                const roomTypesList = [...roomTypeSet].length
                    ? [...roomTypeSet].map((type) => type.charAt(0).toUpperCase() + type.slice(1))
                    : ["Single", "Double", "Triple", "Quad"];
                setRoomTypes(roomTypesList);
                // Removed unused startDate/endDate setting
                // setStartDate([...new Set(data.map((pkg) => pkg["Start Date"]))]);
                // setEndDate([...new Set(data.map((pkg) => pkg["End Date"]))]);

            } catch (error) {
                console.error("Failed to load packages", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const filteredPackages = useMemo(() =>
        packages.filter((pkg) => {
            // Basic Fields
            if (pkg["Starting Price"] < priceRangeSAR[0] || pkg["Starting Price"] > priceRangeSAR[1]) return false;

            const statusValue = (pkg.phase2Status || pkg["Phase 2 Status"] || "").toString().toLowerCase();
            const isSoldOut = statusValue === "sold_out" || statusValue === "soldout";
            if (!includeSoldOut && isSoldOut) return false;

            // Convert SAR to selected currency for filtering
            const rate = CURRENCY_RATES[selectedCurrency];
            if (rate) {
                const convertedPrice = pkg["Starting Price"] * rate;
                if (convertedPrice < priceRangeGBP[0] || convertedPrice > priceRangeGBP[1]) return false;
            }

            if (pkg["Duration_"] < duration[0] || pkg["Duration_"] > duration[1]) return false;
            if (selectedCategory.length > 0 && !selectedCategory.includes(pkg.Category)) return false;
            if (selectedCategoryType.length > 0 && !selectedCategoryType.includes(pkg["Category Type"])) return false;
            if (selectedCamp.length > 0) {
                const matches = selectedCamp.some((camp) => isCampAvailable(pkg, camp));
                if (!matches) return false;
            }
            if (selectedProvider.length > 0 && !selectedProvider.includes(pkg.Provider)) return false;
            if (selectedMadinahHotelZone.length > 0 && !selectedMadinahHotelZone.includes(pkg["Madinah Hotel Zone"])) return false;
            if (selectedMakkahHotelZone.length > 0 && !selectedMakkahHotelZone.includes(pkg["Makkah Hotel Zone"])) return false;
            if (selectedMakkah2HotelZone.length > 0 && !selectedMakkah2HotelZone.includes(pkg["Makkah 2 Hotel Zone"])) return false;
            if (selectedStartDate && pkg["Start Date"] !== selectedStartDate) return false;
            if (selectedEndDate && pkg["End Date"] !== selectedEndDate) return false;

            // Outbound flight filters (onward journey only)
            if (selectedOutboundDeparture) {
                const matches = (pkg.__outboundDepartures || []).some(
                    (departure) => normalizeKey(departure) === normalizeKey(selectedOutboundDeparture)
                );
                if (!matches) return false;
            }
            if (selectedOutboundArrival) {
                const matches = (pkg.__outboundArrivals || []).some(
                    (arrival) => normalizeKey(arrival) === normalizeKey(selectedOutboundArrival)
                );
                if (!matches) return false;
            }

            // Occupancy filters (must be available in all accommodations)
            if (selectedRoomTypes.length > 0) {
                const availableTypes = (pkg.__roomTypesAvailable || []).map(normalizeRoomType);
                const matches = selectedRoomTypes.every(
                    (type) => availableTypes.includes(normalizeRoomType(type))
                );
                if (!matches) return false;
            }

            // Search Query
            if (searchQuery && !Object.values(pkg).some(val => val && val.toString().toLowerCase().includes(searchQuery.toLowerCase()))) {
                return false;
            }

            return true;
        }),
        [packages, priceRangeSAR, priceRangeGBP, duration, selectedCategory, selectedCategoryType, selectedProvider, selectedCamp, selectedOutboundDeparture, selectedOutboundArrival, selectedRoomTypes, selectedMadinahHotelZone, selectedMakkahHotelZone, selectedMakkah2HotelZone, selectedStartDate, selectedEndDate, searchQuery, selectedCurrency, includeSoldOut]
    );

    const soldOutStats = useMemo(() => {
        const total = packages.length;
        const soldOut = packages.filter((pkg) => {
            const statusValue = (pkg.phase2Status || pkg["Phase 2 Status"] || "").toString().toLowerCase();
            return statusValue === "sold_out" || statusValue === "soldout";
        }).length;
        return { total, soldOut };
    }, [packages]);

    // Infinite scroll logic
    const displayedPackages = filteredPackages.slice(0, displayCount);
    const hasMore = displayCount < filteredPackages.length;

    // Reset display count when filters change
    useEffect(() => {
        setDisplayCount(itemsPerLoad);
    }, [filteredPackages.length, itemsPerLoad]);

    // Reset currency price range when currency changes
    useEffect(() => {
        setPriceRangeGBP([currencyBounds.min, currencyBounds.max]);
    }, [selectedCurrency, currencyBounds.min, currencyBounds.max]);

    // Load more handler
    const loadMore = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            setIsLoadingMore(true);
            // Simulate slight delay for smoother UX
            setTimeout(() => {
                setDisplayCount(prev => prev + itemsPerLoad);
                setIsLoadingMore(false);
            }, 300);
        }
    }, [isLoadingMore, hasMore, itemsPerLoad]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && hasMore && !isLoadingMore) {
                    loadMore();
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );

        const sentinel = document.getElementById('scroll-sentinel');
        if (sentinel) {
            observer.observe(sentinel);
        }

        return () => {
            if (sentinel) {
                observer.unobserve(sentinel);
            }
        };
    }, [hasMore, isLoadingMore, loadMore]);

    const MAINTENANCE_MODE = false; // Set to true for maintenance mode

    if (MAINTENANCE_MODE) {
        return (
            <ThemeProvider theme={getTheme("light")}>
                <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" }}>
                    <AppBar position="static">
                        <Container maxWidth="lg">
                            <Toolbar sx={{ py: 1 }}>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        Hajj 2026 Package Explorer
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Your Journey to the Holy Land
                                    </Typography>
                                </Box>
                            </Toolbar>
                        </Container>
                    </AppBar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#002366', mb: 2 }}>
                            Under Maintenance
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', maxWidth: 500 }}>
                            We are currently updating package data. Please check back shortly.
                        </Typography>
                    </Box>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={getTheme("light")}>
            <Box sx={{
                minHeight: { xs: "100vh", md: "auto" },
                height: { xs: "auto", md: "100vh" },
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                position: "relative",
                overflow: { xs: "visible", md: "hidden" },
                "&::before": {
                    content: '""',
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "radial-gradient(circle at 10% 20%, rgba(0,35,102,0.03) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(255,152,0,0.02) 0%, transparent 40%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }
            }}>
                <Header />

                <Container maxWidth="xl" sx={{
                    position: "relative",
                    zIndex: 1,
                    mt: { xs: 0, md: 2 },
                    flex: { xs: 'none', md: 1 },
                    display: { xs: "block", md: "flex" },
                    flexDirection: "column",
                    overflow: { xs: "visible", md: "hidden" },
                    pb: { xs: 10, md: 10 },
                }}>
                    <Grid container spacing={{ xs: 0, md: 3.5 }} sx={{ flex: { xs: 'none', md: 1 }, overflow: { xs: "visible", md: "hidden" } }}>
                        {/* Filters Sidebar - Stays fixed on desktop */}
                        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
                            <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
                                {/* Currency Selector - Shown on desktop above filters */}
                                <Box
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        bgcolor: "background.paper",
                                        borderRadius: "16px",
                                        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        p: 2,
                                        mb: 2,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                        sx={{
                                            mb: 1,
                                            color: "#002366",
                                            fontSize: "0.8rem"
                                        }}
                                    >
                                        Display Currency
                                    </Typography>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{
                                            bgcolor: "#fff",
                                            px: 1,
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                        }}>Currency</InputLabel>
                                        <Select
                                            value={selectedCurrency}
                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                            sx={{
                                                borderRadius: "10px",
                                                fontWeight: 600,
                                                fontSize: "0.85rem",
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#002366",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#002366",
                                                    borderWidth: 2,
                                                },
                                            }}
                                        >
                                            <MenuItem value="GBP">🇬🇧 GBP (£)</MenuItem>
                                            <MenuItem value="USD">🇺🇸 USD ($)</MenuItem>
                                            <MenuItem value="EUR">🇪🇺 EUR (€)</MenuItem>
                                            <MenuItem value="CAD">🇨🇦 CAD (C$)</MenuItem>
                                            <MenuItem value="AUD">🇦🇺 AUD (A$)</MenuItem>
                                            <MenuItem value="CHF">🇨🇭 CHF</MenuItem>
                                            <MenuItem value="JPY">🇯🇵 JPY (¥)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" sx={{
                                        mt: 1,
                                        display: "block",
                                        fontSize: "0.65rem",
                                        color: "#64748b",
                                        fontStyle: "italic",
                                        lineHeight: 1.4,
                                    }}>
                                        Rates as of Jan 10, 2026
                                    </Typography>
                                </Box>

                                <Filters
                                    categories={categories}
                                    categoryTypes={categoryTypes}
                                    outboundDepartures={outboundDepartures}
                                    outboundArrivals={outboundArrivals}
                                    inboundDepartures={inboundDepartures}
                                    inboundArrivals={inboundArrivals}
                                    camps={camps}
                                    roomTypes={roomTypes}
                                    providers={providers}
                                    madinahHotelZone={madinahHotelZone}
                                    makkahHotelZone={makkahHotelZone}
                                    makkah2HotelZone={makkah2HotelZone}
                                    selectedCategory={selectedCategory}
                                    setSelectedCategory={setSelectedCategory}
                                    selectedCategoryType={selectedCategoryType}
                                    setSelectedCategoryType={setSelectedCategoryType}
                                    selectedOutboundDeparture={selectedOutboundDeparture}
                                    setSelectedOutboundDeparture={setSelectedOutboundDeparture}
                                    selectedOutboundArrival={selectedOutboundArrival}
                                    setSelectedOutboundArrival={setSelectedOutboundArrival}
                                    selectedInboundDeparture={selectedInboundDeparture}
                                    setSelectedInboundDeparture={setSelectedInboundDeparture}
                                    selectedInboundArrival={selectedInboundArrival}
                                    setSelectedInboundArrival={setSelectedInboundArrival}
                                    selectedProvider={selectedProvider}
                                    setSelectedProvider={setSelectedProvider}
                                    selectedCamp={selectedCamp}
                                    setSelectedCamp={setSelectedCamp}
                                    selectedRoomTypes={selectedRoomTypes}
                                    setSelectedRoomTypes={setSelectedRoomTypes}
                                    includeSoldOut={includeSoldOut}
                                    setIncludeSoldOut={setIncludeSoldOut}
                                    selectedMadinahHotelZone={selectedMadinahHotelZone}
                                    setSelectedMadinahHotelZone={setSelectedMadinahHotelZone}
                                    selectedMakkahHotelZone={selectedMakkahHotelZone}
                                    setSelectedMakkahHotelZone={setSelectedMakkahHotelZone}
                                    selectedMakkah2HotelZone={selectedMakkah2HotelZone}
                                    setSelectedMakkah2HotelZone={setSelectedMakkah2HotelZone}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    priceRangeSAR={priceRangeSAR}
                                    setPriceRangeSAR={setPriceRangeSAR}
                                    priceRangeGBP={priceRangeGBP}
                                    setPriceRangeGBP={setPriceRangeGBP}
                                    duration={duration}
                                    setDuration={setDuration}
                                    selectedStartDate={selectedStartDate}
                                    setSelectedStartDate={setSelectedStartDate}
                                    selectedEndDate={selectedEndDate}
                                    setSelectedEndDate={setSelectedEndDate}
                                    minPriceSAR={filterBounds.minSAR}
                                    maxPriceSAR={filterBounds.maxSAR}
                                    minPriceGBP={currencyBounds.min}
                                    maxPriceGBP={currencyBounds.max}
                                    minDuration={filterBounds.minDur}
                                    maxDuration={filterBounds.maxDur}
                                    selectedCurrency={selectedCurrency}
                                    currencySymbols={CURRENCY_SYMBOLS}
                                />
                            </Box>
                        </Grid>

                        {/* Results Grid - Scrollable on desktop */}
                        <Grid item xs={12} md={9} sx={{ height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'auto' } }}>
                            {loading ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        py: { xs: 12, md: 16 },
                                        minHeight: 400,
                                    }}
                                >
                                    <CircularProgress
                                        size={60}
                                        thickness={4}
                                        sx={{
                                            color: "#002366",
                                            mb: 3,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        color="text.primary"
                                        fontWeight={600}
                                        sx={{ fontSize: "1.1rem" }}
                                    >
                                        Loading packages...
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1, fontSize: "0.9rem" }}
                                    >
                                        Please wait while we fetch the best Hajj packages for you
                                    </Typography>
                                </Box>
                            ) : (
                                <Fade in={!loading} timeout={600}>
                                    <Box sx={{ display: "flex", flexDirection: "column", height: { xs: 'auto', md: 'auto' }, overflow: { xs: 'visible', md: 'visible' } }}>
                                        {/* Currency and Filters Chips Row - Sticky bar on mobile */}
                                        <Box sx={{
                                            mt: { xs: 0, md: 0 },
                                            mb: { xs: 1.5, md: 3 },
                                            py: { xs: 1.5, md: 0 },
                                            px: { xs: 2, md: 0 },
                                            mx: { xs: -2, md: 0 },
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: { xs: 1, md: 2 },
                                            flexShrink: 0,
                                            position: { xs: 'sticky', md: 'static' },
                                            top: { xs: 0, md: 'auto' },
                                            bgcolor: { xs: '#f5f7fa', md: 'transparent' },
                                            zIndex: { xs: 100, md: 'auto' },
                                            borderBottom: { xs: '1px solid rgba(0,35,102,0.3)', md: 'none' },
                                            boxShadow: { xs: '0 2px 8px rgba(0,0,0,0.08)', md: 'none' },
                                        }}>
                                            {/* Top Row: Currency & Filters Button */}
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                                {/* Currency Selector - Hidden on desktop */}
                                                <FormControl size="small" sx={{ display: { xs: 'block', md: 'none' } }}>
                                                    <Select
                                                        value={selectedCurrency}
                                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                                        sx={{
                                                            minWidth: 90,
                                                            height: 36,
                                                            borderRadius: "10px",
                                                            fontWeight: 700,
                                                            fontSize: "0.85rem",
                                                            bgcolor: "#002366",
                                                            color: "#fff",
                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#002366",
                                                            },
                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#001a4d",
                                                            },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                borderColor: "#001a4d",
                                                            },
                                                            "& .MuiSvgIcon-root": {
                                                                color: "#fff",
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="GBP">🇬🇧 GBP</MenuItem>
                                                        <MenuItem value="USD">🇺🇸 USD</MenuItem>
                                                        <MenuItem value="EUR">🇪🇺 EUR</MenuItem>
                                                        <MenuItem value="CAD">🇨🇦 CAD</MenuItem>
                                                        <MenuItem value="AUD">🇦🇺 AUD</MenuItem>
                                                        <MenuItem value="CHF">🇨🇭 CHF</MenuItem>
                                                        <MenuItem value="JPY">🇯🇵 JPY</MenuItem>
                                                    </Select>
                                                </FormControl>

                                                {/* Filters Button (Mobile) */}
                                                <Chip
                                                    label="Filters"
                                                    icon={<FilterListIcon />}
                                                    onClick={() => {
                                                        const filterBtn = document.querySelector('[data-filter-button]');
                                                        if (filterBtn) filterBtn.click();
                                                    }}
                                                    sx={{
                                                        display: { xs: "flex", md: "none" },
                                                        height: 36,
                                                        px: 1.5,
                                                        fontSize: "0.85rem",
                                                        bgcolor: "rgba(0,35,102,0.08)",
                                                        color: "#002366",
                                                        fontWeight: 700,
                                                        borderRadius: "10px",
                                                        border: "1px solid rgba(0,35,102,0.3)",
                                                        ml: "auto", // Push to right if currency is hidden or alone
                                                        cursor: "pointer",
                                                        "& .MuiChip-icon": {
                                                            color: "#002366",
                                                            fontSize: "20px",
                                                        },
                                                        "&:hover": {
                                                            bgcolor: "rgba(0,35,102,0.15)",
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            {/* Active Filter Chips - Scrollable */}
                                            {(selectedCategory?.length > 0 || selectedProvider?.length > 0 || selectedMakkahHotelZone?.length > 0 || selectedMakkah2HotelZone?.length > 0 || selectedMadinahHotelZone?.length > 0 || searchQuery || !includeSoldOut) && (
                                                <Box sx={{
                                                    display: "flex",
                                                    gap: 1,
                                                    flexWrap: { xs: "nowrap", md: "wrap" },
                                                    overflowX: { xs: "auto", md: "visible" },
                                                    alignItems: "center",
                                                    flexShrink: 0,
                                                    width: "100%",
                                                    scrollbarWidth: "none",
                                                    "&::-webkit-scrollbar": { display: "none" },
                                                    maskImage: { xs: "linear-gradient(to right, black 90%, transparent 100%)", md: "none" },
                                                    WebkitMaskImage: { xs: "linear-gradient(to right, black 90%, transparent 100%)", md: "none" },
                                                }}>
                                                    {selectedCategory?.length > 0 && selectedCategory.map((val) => (
                                                        <Chip
                                                            key={`cat-${val}`}
                                                            label={val}
                                                            size="small"
                                                            onDelete={() => setSelectedCategory(selectedCategory.filter(item => item !== val))}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {selectedProvider?.length > 0 && selectedProvider.map((val) => (
                                                        <Chip
                                                            key={`prov-${val}`}
                                                            label={val}
                                                            size="small"
                                                            onDelete={() => setSelectedProvider(selectedProvider.filter(item => item !== val))}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {selectedMakkahHotelZone?.length > 0 && selectedMakkahHotelZone.map((val) => (
                                                        <Chip
                                                            key={`makkah-${val}`}
                                                            label={`Makkah: ${val}`}
                                                            size="small"
                                                            onDelete={() => setSelectedMakkahHotelZone(selectedMakkahHotelZone.filter(item => item !== val))}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {selectedMakkah2HotelZone?.length > 0 && selectedMakkah2HotelZone.map((val) => (
                                                        <Chip
                                                            key={`makkah2-${val}`}
                                                            label={`Makkah 2: ${val}`}
                                                            size="small"
                                                            onDelete={() => setSelectedMakkah2HotelZone(selectedMakkah2HotelZone.filter(item => item !== val))}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {selectedMadinahHotelZone?.length > 0 && selectedMadinahHotelZone.map((val) => (
                                                        <Chip
                                                            key={`madinah-${val}`}
                                                            label={`Madinah: ${val}`}
                                                            size="small"
                                                            onDelete={() => setSelectedMadinahHotelZone(selectedMadinahHotelZone.filter(item => item !== val))}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                    {!includeSoldOut && (
                                                        <Chip
                                                            label="Only available"
                                                            size="small"
                                                            onClick={() => setIncludeSoldOut(true)}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(211,47,47,0.08)",
                                                                color: "#b71c1c",
                                                                fontWeight: 700,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(211,47,47,0.35)",
                                                                flexShrink: 0,
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    )}
                                                    {includeSoldOut && (
                                                        <Chip
                                                            label="Including sold out"
                                                            size="small"
                                                            onClick={() => setIncludeSoldOut(false)}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 700,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.25)",
                                                                flexShrink: 0,
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    )}
                                                    {searchQuery && (
                                                        <Chip
                                                            label={`"${searchQuery}"`}
                                                            size="small"
                                                            onDelete={() => setSearchQuery("")}
                                                            sx={{
                                                                height: 28,
                                                                px: 0.5,
                                                                fontSize: "0.75rem",
                                                                bgcolor: "rgba(0,35,102,0.08)",
                                                                color: "#002366",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                border: "1px solid rgba(0,35,102,0.2)",
                                                                flexShrink: 0,
                                                                "& .MuiChip-deleteIcon": {
                                                                    color: "#002366",
                                                                    fontSize: "18px",
                                                                    "&:hover": { color: "#001a4d" }
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                            <Typography variant="body2" sx={{ color: "#6b6f76", fontSize: "0.85rem" }}>
                                                Sold out: {soldOutStats.soldOut} out of {soldOutStats.total} packages
                                            </Typography>
                                        </Box>

                                        {/* Results Count Line (Sticky on Mobile) */}
                                        <Box sx={{
                                            display: { xs: "flex", md: "none" }, // Mobile specific layout
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            flexShrink: 0,
                                            mt: 1,
                                            width: "100%",
                                        }}>
                                            <Box>
                                                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: "1rem" }}>
                                                    {filteredPackages.length} {filteredPackages.length === 1 ? 'Package' : 'Packages'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                                                    {displayedPackages.length > 0 ? `Showing ${displayedPackages.length} of ${filteredPackages.length}` : 'No results found'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Results Count Line (Desktop only) */}
                                        <Box sx={{
                                            display: { xs: "none", md: "flex" }, // Hide on mobile since it's in sticky header
                                            mb: 3,
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            flexShrink: 0,
                                        }}>
                                            <Box>
                                                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: "1.1rem" }}>
                                                    {filteredPackages.length} {filteredPackages.length === 1 ? 'Package' : 'Packages'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                                                    {displayedPackages.length > 0 ? `Showing ${displayedPackages.length} of ${filteredPackages.length}` : 'No results found'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Package List Container */}
                                        <Box sx={{
                                            flex: 'none',
                                        }}>
                                            {displayedPackages.length > 0 ? (
                                                <Grid container spacing={{ xs: 2, md: 3.5 }}>
                                                    {displayedPackages.map((pkg, index) => (
                                                        <Grid item xs={12} sm={6} lg={4} key={pkg.id || index}>
                                                            <Box height="100%">
                                                                <PackageCard
                                                                    packageData={pkg}
                                                                    selectedCurrency={selectedCurrency}
                                                                    currencyRates={CURRENCY_RATES}
                                                                    currencySymbols={CURRENCY_SYMBOLS}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        textAlign: "center",
                                                        py: { xs: 8, md: 12 },
                                                        px: 4,
                                                        bgcolor: "#fff",
                                                        borderRadius: "20px",
                                                        border: "2px dashed rgba(0,35,102,0.2)",
                                                        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 80,
                                                            height: 80,
                                                            borderRadius: "50%",
                                                            bgcolor: "rgba(0,35,102,0.08)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            mx: "auto",
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <SearchOffIcon sx={{ fontSize: 48, color: "#002366" }} />
                                                    </Box>
                                                    <Typography variant="h5" color="text.primary" gutterBottom fontWeight={700} sx={{ fontSize: "1.5rem" }}>
                                                        No packages found
                                                    </Typography>
                                                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mx: "auto", mb: 3, lineHeight: 1.6, fontSize: "1rem" }}>
                                                        We couldn't find any packages matching your filters. Try adjusting your search criteria or clearing some filters.
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>
                                                        💡 Tip: Try reducing the number of active filters or expanding your price range
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Infinite Scroll Sentinel & Loading Indicator */}
                                            {displayedPackages.length > 0 && (
                                                <>
                                                    <div id="scroll-sentinel" style={{ height: '1px' }} />
                                                    {isLoadingMore && (
                                                        <Box sx={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            mt: 4,
                                                            py: 3,
                                                        }}>
                                                            <CircularProgress
                                                                size={40}
                                                                thickness={4}
                                                                sx={{ color: "#002366" }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ ml: 2, fontWeight: 600 }}
                                                            >
                                                                Loading more packages...
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {!hasMore && displayedPackages.length > 0 && (
                                                        <Box sx={{
                                                            textAlign: "center",
                                                            mt: 6,
                                                            pt: 4,
                                                            borderTop: "1px solid rgba(0,0,0,0.06)",
                                                        }}>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                                                            >
                                                                You've reached the end • All {filteredPackages.length} packages displayed
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </Fade>
                            )}
                        </Grid>
                    </Grid>
                </Container>

                <Footer />
            </Box>
        </ThemeProvider >
    );
}
